import { useCallback, useEffect, useState } from 'react';
import CloneUtils from '../../../../utils/cloneUtils';
import ImportsRepo from '../../../../utils/repository/importsRepo';
import { BLOCK_TYPE } from '../constants/generalConstants';
import { useSetMappingStoreState } from '../stores/mappingStore';
import { useSetMetadataStoreState } from '../stores/metadataStore';
import { useSetBoundingBoxesStoreState } from '../stores/boundingBoxesStore';
import { AutomaticDataModesType, ExtractionType, ImportTableType, TableBlockType } from '../types';
import {
    getRowMappings,
    getRowMappingsConfidences,
    getTablesCellsBoundingBoxesMap,
    getTablesRowsBoundingBoxesMap,
} from '../utils/tableExtraction';
import { useImageStoreActions } from '../stores/imageStore';

const BASE_IMAGE_URL = 'https://3d-stockpicker-reports.s3-accelerate.amazonaws.com/';

function useExtraction(importId: string | undefined, automaticDataModes: AutomaticDataModesType) {
    const [extraction, setExtraction] = useState<ExtractionType | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const setMetadataStoreState = useSetMetadataStoreState();
    const setBoundingBoxesStoreState = useSetBoundingBoxesStoreState();
    const setMappingStoreState = useSetMappingStoreState();
    const { setImage } = useImageStoreActions();

    const setStates = useCallback((extraction: ExtractionType) => {
        const tableBlocks = extraction.document.blocks.filter(
            (block) => block.type === BLOCK_TYPE.TABLE
        ) as TableBlockType[];
        setBoundingBoxesStoreState(
            getTablesRowsBoundingBoxesMap(tableBlocks),
            getTablesCellsBoundingBoxesMap(tableBlocks)
        );
        const rowMappings = getRowMappings(extraction.mapping.rowMappings);
        const rowMappingsConfidencesMap = getRowMappingsConfidences(rowMappings);
        setMappingStoreState({
            draggedItemId: undefined,
            confidencesMap: rowMappingsConfidencesMap,
            highlightedMappingsMap: {},
            mappings: rowMappings,
            draggedOverItemId: undefined,
        });
        setMetadataStoreState(CloneUtils.deepClone(extraction.metadata));
        setExtraction(extraction);

        const img = new Image();
        img.onload = () => {
            setImage(img);
        };
        img.src = BASE_IMAGE_URL + extraction.s3Key;

        return () => {
            setImage(null);
        };
    }, []);

    useEffect(() => {
        if (importId) {
            setIsLoading(true);
            ImportsRepo.get(importId, { ...automaticDataModes }, (res: { data: ExtractionType }) => {
                setStates(res.data);
                setIsLoading(false);
            });
        }
    }, [importId, setStates, automaticDataModes]);

    const setImportTable = useCallback((importTable: ImportTableType) => {
        setExtraction((prev) => {
            if (prev) {
                return {
                    ...prev,
                    importTable,
                };
            }
            return null;
        });
    }, []);

    return { extraction, isLoading, setImportTable, setStates };
}

export default useExtraction;
